// Portable route locations and logical destinations shared by apps and hosts.
/** A guest-space location: root-relative path plus '' or one well-formed '?...' search. */
export type GuestRouteLocation = {
  path: string
  search: string
}
// ── Destinations: links out of the app, by logical id ──
// A link to a platform object or another app names it logically; the host resolves the id to its
// existing route. In the guest, such a link is a reserved root-relative href — `/~doss/<kind>/<id>`,
// plus an app's own path and search — so every string `to` (React Router, ARP) can carry it. The
// frame turns it into a destination request; it never reaches the host as a URL.

export const GUEST_DESTINATION_KINDS = ['table', 'form', 'chart', 'dashboard', 'app'] as const
export type GuestDestinationKind = (typeof GUEST_DESTINATION_KINDS)[number]

export type GuestDestination = {
  kind: GuestDestinationKind
  /** The object's logical id — an app's is the id declared at `application('…')`. */
  logicalId: string
  /** `app` only: the route within that app, a sanitized guest path. */
  path?: string
  /** `app` only: '' or a sanitized '?…'. */
  search?: string
}

/** The first path segment of every destination href, which an app's own routes can't use. */
export const GUEST_DESTINATION_SEGMENT = '~doss'
/** `/${GUEST_DESTINATION_SEGMENT}/`, spelled as a literal so bundles that never read it drop it. */
export const GUEST_DESTINATION_PREFIX = '/~doss/'
/** The reserved href a destination travels as inside the guest. */
export const formatDestinationHref = (destination: GuestDestination): string => {
  const base = `${GUEST_DESTINATION_PREFIX}${destination.kind}/${destination.logicalId}`
  const path = destination.path === undefined || destination.path === '/' ? '' : destination.path
  return `${base}${path}${destination.search ?? ''}`
}
